import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  updateDoc, 
  addDoc,
  query,
  where,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Prize } from '../types/prize';
import { PrizeNotification } from '../types/prizeNotification';
import { getUserProfile } from './userService';

/**
 * Share a prize to multiple friends
 * Updates original prize and creates new prize documents for recipients
 */
export const sharePrize = async (
  prizeId: string,
  senderId: string,
  recipientIds: string[],
  giftMessage?: string
): Promise<void> => {
  try {
    if (recipientIds.length === 0) {
      throw new Error('No recipients selected');
    }
    
    if (giftMessage && giftMessage.length > 80) {
      throw new Error('Gift message must be 80 characters or less');
    }
    
    // Get the original prize
    const prizeRef = doc(db, 'prizes', prizeId);
    const prizeDoc = await getDoc(prizeRef);
    
    if (!prizeDoc.exists()) {
      throw new Error('Prize not found');
    }
    
    const prizeData = prizeDoc.data() as Prize;
    
    // Verify the sender owns the prize
    if (prizeData.userId !== senderId) {
      throw new Error('You do not own this prize');
    }
    
    // Verify prize is not redeemed
    if (prizeData.isRedeemed) {
      throw new Error('Cannot share a redeemed prize');
    }
    
    // Verify prize hasn't already been shared
    if (prizeData.beenShared) {
      throw new Error('This prize has already been shared');
    }
    
    // Get sender information
    const senderProfile = await getUserProfile(senderId);
    if (!senderProfile) {
      throw new Error('Sender profile not found');
    }
    
    const senderUsername = senderProfile.brugernavn || `${senderProfile.fornavn} ${senderProfile.efternavn}`;
    
    // Get recipient usernames
    const recipientUsernames: string[] = [];
    const recipientProfiles = await Promise.all(
      recipientIds.map(id => getUserProfile(id))
    );
    
    recipientProfiles.forEach((profile) => {
      if (profile) {
        const username = profile.brugernavn || `${profile.fornavn} ${profile.efternavn}`;
        recipientUsernames.push(username);
      }
    });
    
    // Determine the original winner ID
    const originalWinnerId = prizeData.originalWinnerId || prizeData.userId;
    
    // Create new prize documents for each recipient
    const prizesCollection = collection(db, 'prizes');
    const notificationsCollection = collection(db, 'prize_notifications');
    
    for (let i = 0; i < recipientIds.length; i++) {
      const recipientId = recipientIds[i];
      const recipientUsername = recipientUsernames[i] || 'Unknown';
      
      // Create new prize for recipient
      const newPrizeData: Omit<Prize, 'id'> = {
        userId: recipientId,
        campaignId: prizeData.campaignId,
        brandName: prizeData.brandName,
        brandLogo: prizeData.brandLogo,
        prizeTitle: prizeData.prizeTitle,
        prizeValue: prizeData.prizeValue,
        category: prizeData.category,
        claimedAt: new Date(),
        expiresAt: prizeData.expiresAt,
        isRedeemed: false,
        beenShared: false,
        sharedTo: [],
        sharedToUsernames: [],
        senderId: senderId,
        originalWinnerId: originalWinnerId
      };
      
      // Only add optional fields if they have values
      if (prizeData.qrCode) {
        newPrizeData.qrCode = prizeData.qrCode;
      }
      
      if (giftMessage) {
        newPrizeData.giftMessage = giftMessage;
      }
      
      const newPrizeRef = await addDoc(prizesCollection, newPrizeData);
      
      // Create notification for recipient
      const notificationData: Omit<PrizeNotification, 'id'> = {
        receiverId: recipientId,
        senderId: senderId,
        senderUsername: senderUsername,
        prizeId: newPrizeRef.id,
        prizeName: prizeData.prizeTitle,
        brandLogo: prizeData.brandLogo,
        createdAt: new Date(),
        isRead: false
      };
      
      // Only add giftMessage if it exists
      if (giftMessage) {
        notificationData.giftMessage = giftMessage;
      }
      
      await addDoc(notificationsCollection, notificationData);
    }
    
    // Update original prize to mark as shared
    await updateDoc(prizeRef, {
      beenShared: true,
      sharedTo: recipientIds,
      sharedToUsernames: recipientUsernames,
      sharedAt: new Date()
    });
    
    // Increment sender's shared prizes counter (count each prize as 1, not per recipient)
    const senderRef = doc(db, 'users', senderId);
    await updateDoc(senderRef, {
      antal_delte_praemier: increment(1)
    });
    
  } catch (error) {
    console.error('Error sharing prize:', error);
    throw error;
  }
};

/**
 * Get unread prize notifications for a user
 * Groups notifications by sender
 */
export const getPrizeNotifications = async (userId: string): Promise<{
  [senderId: string]: PrizeNotification[]
}> => {
  try {
    const notificationsRef = collection(db, 'prize_notifications');
    const q = query(
      notificationsRef,
      where('receiverId', '==', userId),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(q);
    const notifications: PrizeNotification[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      notifications.push({
        id: doc.id,
        receiverId: data.receiverId,
        senderId: data.senderId,
        senderUsername: data.senderUsername,
        prizeId: data.prizeId,
        prizeName: data.prizeName,
        brandLogo: data.brandLogo,
        giftMessage: data.giftMessage,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        isRead: data.isRead
      });
    });
    
    // Group by sender
    const grouped: { [senderId: string]: PrizeNotification[] } = {};
    notifications.forEach((notification) => {
      if (!grouped[notification.senderId]) {
        grouped[notification.senderId] = [];
      }
      grouped[notification.senderId].push(notification);
    });
    
    return grouped;
  } catch (error) {
    console.error('Error getting prize notifications:', error);
    throw error;
  }
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'prize_notifications', notificationId);
    await updateDoc(notificationRef, {
      isRead: true
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark multiple notifications as read
 */
export const markNotificationsAsRead = async (notificationIds: string[]): Promise<void> => {
  try {
    const promises = notificationIds.map(id => markNotificationAsRead(id));
    await Promise.all(promises);
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw error;
  }
};

/**
 * Get prizes that a user has shared to others
 */
export const getSharedPrizes = async (userId: string): Promise<Prize[]> => {
  try {
    const prizesRef = collection(db, 'prizes');
    const q = query(
      prizesRef,
      where('userId', '==', userId),
      where('beenShared', '==', true)
    );
    
    const snapshot = await getDocs(q);
    const prizes: Prize[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      prizes.push({
        id: doc.id,
        userId: data.userId,
        campaignId: data.campaignId,
        brandName: data.brandName,
        brandLogo: data.brandLogo,
        prizeTitle: data.prizeTitle,
        prizeValue: data.prizeValue,
        category: data.category,
        claimedAt: data.claimedAt?.toDate?.() || new Date(),
        expiresAt: data.expiresAt?.toDate?.() || new Date(),
        isRedeemed: data.isRedeemed || false,
        redeemedAt: data.redeemedAt?.toDate?.(),
        qrCode: data.qrCode,
        beenShared: data.beenShared || false,
        sharedTo: data.sharedTo || [],
        sharedToUsernames: data.sharedToUsernames || [],
        senderId: data.senderId,
        originalWinnerId: data.originalWinnerId,
        sharedAt: data.sharedAt?.toDate?.(),
        giftMessage: data.giftMessage
      });
    });
    
    return prizes;
  } catch (error) {
    console.error('Error getting shared prizes:', error);
    throw error;
  }
};
